import React from 'react';
import { useTranslation } from 'react-i18next';
import { CardDetailResponse } from '__generated__/graphql';
import MasterCard from '../../../../assets/images/mastercard.png';
import Visa from '../../../../assets/images/visa.png';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit-icon.svg';

export function selectImage(scheme?: string | null): JSX.Element | null {
  if (!scheme) {
    return null;
  }

  switch (scheme.toLowerCase()) {
    case 'mastercard': {
      return <img src={MasterCard} width="28" alt="mastercard" />;
    }
    case 'visa': {
      return <img src={Visa} width="28" alt="visa" />;
    }
    default:
      return null;
  }
}

interface CardProps {
  card: CardDetailResponse;
  expired?: boolean;
  onEditCardClicked: (card: CardDetailResponse) => void;
}

export default function Card({ card, expired, onEditCardClicked }: CardProps) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex flex-col p-4 rounded-xl border w-[220px] h-[128px] gap-2 transition-all duration-200 ${
        expired
          ? 'border-gray-300 dark:border-slate-600 bg-gray-100 dark:bg-slate-800/50 text-gray-500 dark:text-gray-500'
          : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-md dark:hover:shadow-slate-900/50 hover:border-gray-300 dark:hover:border-slate-600'
      }`}
    >
      <div className="flex items-center">
        {selectImage(card.scheme)}
        <button
          onClick={() => onEditCardClicked(card)}
          className="ml-auto p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          <EditIcon
            className="w-4 h-4 text-gray-500 dark:text-gray-400"
            aria-label={t('account:payments.manageCard.title')}
          />
        </button>
      </div>
      <p className={`text-sm font-medium ${expired ? '' : 'text-gray-900 dark:text-white'}`}>{card.clientName}</p>
      <div
        className={`w-full text-sm font-mono ${expired ? '' : 'text-gray-600 dark:text-gray-300'}`}
      >{`•••• •••• •••• ${card.last4}`}</div>
      <div className="flex items-center">
        <div
          className={`text-xs ${
            expired ? 'text-red-500 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {expired ? t('account:payments.addCard.expired') : card.expiry}
        </div>
        {card.defaultMitFlag && (
          <div
            className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${
              expired
                ? 'bg-gray-300 dark:bg-slate-600 text-gray-500 dark:text-gray-400'
                : 'bg-orange/10 dark:bg-teal-500/10 text-orange dark:text-teal-400'
            }`}
          >
            {t('common:default')}
          </div>
        )}
      </div>
    </div>
  );
}
